import {useFormik} from 'formik';
import {useSchemes} from '../../../shared';
import {UpdateFundraiserDTO, FundraiserDetailedModel, useFundraiser} from '../../../entities';
import * as Yup from 'yup';
import {AxiosError} from 'axios';
import {useNavigate, useParams} from 'react-router';

type UseEditFundraiserFormProps = {
  navigateAfterSuccess?: boolean;
};

export const useEditFundraiserForm = ({navigateAfterSuccess = true}: UseEditFundraiserFormProps = {}) => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {nameSchema, shortNameSchema, shortDescriptionSchema, detailedDescriptionSchema} = useSchemes();

  const {getFundraiserById, updateFundraiserMutation} = useFundraiser({
    id: id || '',
  });

  const {data: fundraiserData, isLoading: isFundraiserLoading} = getFundraiserById;
  const fundraiser = fundraiserData as FundraiserDetailedModel | null;

  const onSubmit = async (values: any) => {
    try {
      if (id) {
        const updateData: UpdateFundraiserDTO = {
          general: {
            name: values.name,
            shortName: values.shortName || null,
            shortDescription: values.shortDescription,
            detailedDescription: values.detailedDescription,
            fundId: values.fundId,
            regionId: values.regionId,
            categoryIds: values.categoryIds,
            externalId: values.externalId,
          },
        };

        await updateFundraiserMutation.mutateAsync(updateData);
        if (navigateAfterSuccess) {
          navigate(`/fundraisings/appeals/${id}`);
        }
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: fundraiser?.name || '',
      shortName: fundraiser?.shortName ?? '',
      shortDescription: fundraiser?.shortDescription || '',
      detailedDescription: fundraiser?.detailedDescription || '',
      fundId: fundraiser?.fund?.id || '',
      regionId: fundraiser?.region?.id || '',
      categoryIds: fundraiser?.categories?.map((cat) => cat.id) || [],
      externalId: fundraiser?.externalId || null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: nameSchema,
      shortName: shortNameSchema,
      shortDescription: shortDescriptionSchema,
      detailedDescription: detailedDescriptionSchema,
      fundId: Yup.string().required('Фонд обязателен'),
      regionId: Yup.string().required('Регион обязателен'),
      categoryIds: Yup.array().min(1, 'Выберите хотя бы одну категорию'),
    }),
    onSubmit,
  });

  return {
    ...formik,
    isLoading: updateFundraiserMutation.isPending || isFundraiserLoading,
    submitError: updateFundraiserMutation.error
      ? (updateFundraiserMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    submitForm: () => formik.handleSubmit(),
  };
};
